/**
 * Mall Map — data generator.
 *
 * Turns the static roster in mallSource.js into fully-specified units: plan
 * geometry, lease terms, trading performance, operations. Everything is derived
 * from a SEEDED PRNG keyed on the unit code, so the numbers are stable across
 * reloads, re-renders and route changes — a unit that ranks #3 stays #3, and
 * the sales chart doesn't reshuffle when the panel re-opens.
 *
 * This is demonstration data. It is internally consistent (rent/sqm, occupancy
 * cost ratio, sales-per-sqm and footfall all reconcile) but it is not wired to
 * the V3 API. Swapping in real data means replacing `buildUnits()` only.
 */

import {
    ZONES, STRIPS, ANCHOR_BLOCKS, FLOORS, AMENITIES,
    CATEGORY_COLORS, STATUS_COLORS, CATEGORIES,
    BRAND_POOL, KIOSK_POOL, FLOOR_MIX, ANCHOR_PLAN, VACANCY_RATE,
    H_CORRIDORS, SPINE, H_SPAN, CORRIDOR_HALF, SPINE_HALF, UNIT_DEPTH,
} from './mallSource';

export {
    ZONES, STRIPS, FLOORS, AMENITIES, CATEGORY_COLORS, STATUS_COLORS, CATEGORIES,
    ANCHOR_BLOCKS, H_CORRIDORS, SPINE, H_SPAN, CORRIDOR_HALF, SPINE_HALF, UNIT_DEPTH,
};

/** Data "as of" date. Fixed so every derived figure is reproducible. */
export const AS_OF = new Date(2026, 7, 1); // 1 Aug 2026

// ── deterministic randomness ────────────────────────────────────────────────

function hashString(str) {
    let h = 2166136261 >>> 0;
    for (let i = 0; i < str.length; i++) {
        h ^= str.charCodeAt(i);
        h = Math.imul(h, 16777619) >>> 0;
    }
    return h >>> 0;
}

/** mulberry32 — small, fast, good enough for demo data. */
function makeRng(seed) {
    let a = seed >>> 0;
    return function rng() {
        a = (a + 0x6D2B79F5) >>> 0;
        let t = a;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

const between = (rng, lo, hi) => lo + rng() * (hi - lo);
const pick = (rng, arr) => arr[Math.floor(rng() * arr.length) % arr.length];

// ── economic model ──────────────────────────────────────────────────────────

/** Annual trading density (AED per sqm) and annual base rent (AED per sqm). */
const CATEGORY_ECONOMICS = {
    'Fashion': { sales: 14000, rent: 2400, atv: 320, conv: 24 },
    'Luxury': { sales: 40000, rent: 5200, atv: 4200, conv: 12 },
    'Jewellery & Watches': { sales: 34000, rent: 4400, atv: 3100, conv: 10 },
    'Beauty': { sales: 22000, rent: 3200, atv: 240, conv: 31 },
    'Electronics': { sales: 20000, rent: 2200, atv: 780, conv: 18 },
    'F&B': { sales: 18000, rent: 2800, atv: 115, conv: 46 },
    'Entertainment': { sales: 8000, rent: 1100, atv: 180, conv: 38 },
    'Sports': { sales: 12000, rent: 1600, atv: 340, conv: 22 },
    'Footwear & Accessories': { sales: 17000, rent: 2600, atv: 290, conv: 26 },
    'Kids & Toys': { sales: 11000, rent: 1500, atv: 210, conv: 27 },
    'Department Store': { sales: 8000, rent: 1000, atv: 400, conv: 20 },
    'Supermarket': { sales: 13000, rent: 850, atv: 165, conv: 68 },
    'Pharmacy & Health': { sales: 16000, rent: 2100, atv: 130, conv: 52 },
    'Banking & Exchange': { sales: 6000, rent: 1800, atv: 260, conv: 44 },
    'Home & Lifestyle': { sales: 7000, rent: 900, atv: 260, conv: 19 },
    'Grocery': { sales: 12000, rent: 900, atv: 145, conv: 62 },
    'Services': { sales: 14000, rent: 2000, atv: 190, conv: 41 },
    'Leisure': { sales: 5000, rent: 700, atv: 260, conv: 34 },
};

/** Prime-space premium by floor. Ground trades hardest, LG softest. */
const FLOOR_FACTOR = { GF: 1.15, L1: 1.0, MZ: 0.95, LG: 0.85 };

/** Seasonality by calendar month (0=Jan). Gulf pattern: Ramadan/Eid up, summer down. */
const SEASONALITY = [1.05, 0.95, 1.10, 1.18, 0.98, 0.88, 0.85, 0.92, 1.00, 1.06, 1.12, 1.28];

/** Anchors sign on a fraction of open-market rent in exchange for footfall draw. */
const ANCHOR_RENT_DISCOUNT = 0.45;

// ── roster + plan geometry ──────────────────────────────────────────────────

/**
 * Cast a floor's tenants to FILL its shopfront runs.
 *
 * The roster is capacity-driven, not a fixed list: we keep drawing from the
 * brand pool until the floor's frontage is ~99% committed, then pack the last
 * few metres with kiosks. That is what makes the plate read like a real mall
 * instead of blocks marooned on concrete — a hand-written roster is always
 * either short (gaps) or long (overflow) the moment the plan changes.
 *
 * Categories are drawn on a weighted round-robin from FLOOR_MIX, so each level
 * keeps its character: services and grocery below, luxury on the ground, kids
 * and sport on the mezzanine, dining up top.
 */
function buildFloorRoster(floor, capacity, rng, taken = []) {
    const mix = FLOOR_MIX[floor.id];
    const categories = Object.keys(mix);

    // Expand the weights into a draw order, then rotate it per floor so two
    // floors with a similar mix don't lay out in the same sequence.
    const order = [];
    categories.forEach((cat) => {
        for (let i = 0; i < mix[cat]; i++) order.push(cat);
    });
    for (let i = order.length - 1; i > 0; i--) {
        const j = Math.floor(rng() * (i + 1));
        [order[i], order[j]] = [order[j], order[i]];
    }

    // Per-category cursor into the pool, offset by floor so the same brands
    // don't always land on the same level.
    const cursor = {};
    categories.forEach((cat, i) => {
        const pool = BRAND_POOL[cat] || [];
        cursor[cat] = pool.length ? Math.floor(rng() * pool.length) + i : 0;
    });

    // Seed with the anchor names, or the pool happily casts a second Uniqlo
    // down the arcade from the Uniqlo anchor.
    const usedOnFloor = new Set(taken);
    const roster = [];
    let committed = 0;
    const target = capacity * 0.99;
    let guard = 0;

    while (committed < target && guard < 4000) {
        guard += 1;
        const cat = order[guard % order.length];
        const pool = BRAND_POOL[cat];
        if (!pool || !pool.length) continue;

        // Walk the pool for the next brand not already trading on this floor.
        let picked = null;
        for (let i = 0; i < pool.length; i++) {
            const entry = pool[(cursor[cat] + i) % pool.length];
            if (!usedOnFloor.has(entry[0])) {
                picked = entry;
                cursor[cat] = (cursor[cat] + i + 1) % pool.length;
                break;
            }
        }
        if (!picked) continue;   // this category is exhausted on this floor

        const remaining = target - committed;
        if (picked[1] > remaining) continue;   // too wide for what's left

        usedOnFloor.add(picked[0]);
        roster.push({ name: picked[0], category: cat, width: picked[1] });
        committed += picked[1];
    }

    // Pack the tail with kiosks — the smallest units, so they fit the offcuts.
    let kioskIdx = Math.floor(rng() * KIOSK_POOL.length);
    let kioskGuard = 0;
    while (committed < target && kioskGuard < 400) {
        kioskGuard += 1;
        const [name, cat, width] = KIOSK_POOL[kioskIdx % KIOSK_POOL.length];
        kioskIdx += 1;
        if (width > target - committed) break;
        roster.push({ name, category: cat, width, kiosk: true });
        committed += width;
    }

    // Sprinkle vacancy through the roster so it isn't clustered at one end.
    roster.forEach((t) => {
        if (!t.kiosk && rng() < VACANCY_RATE) t.status = 'Vacant';
    });

    return roster;
}

/**
 * Lay tenants out along the strips, packed head to tail with no deliberate
 * gaps. Anchors bypass the flow and take a compass block.
 *
 * Distribution is PROPORTIONAL across every run rather than sequential: filling
 * runs one at a time packed each floor into the first arcade and left the rest
 * bare. Each run takes the same fraction of its own length, so the whole plate
 * populates evenly whatever the roster size.
 */
function layoutFloor(floorId, tenants) {
    const placed = [];

    const anchors = tenants.filter((t) => t.anchor);
    const inline = tenants.filter((t) => !t.anchor);

    anchors.forEach((tenant) => {
        const block = ANCHOR_BLOCKS[tenant.anchor];
        placed.push({
            tenant,
            geom: { x: block.x, z: block.z, w: block.w, d: block.d },
            frontage: { x: block.frontage.x, z: block.frontage.z },
            corridor: { axis: 'x', pos: block.frontage.z },
            stripId: `anchor-${tenant.anchor}`,
            isAnchor: true,
        });
    });

    const stripLen = (s) => s.to - s.from;
    const totalCapacity = STRIPS.reduce((sum, s) => sum + stripLen(s), 0);
    const totalWidth = inline.reduce((sum, t) => sum + t.width, 0);
    const fillRatio = Math.min(1, totalWidth / totalCapacity);

    // Track occupancy PER RUN. A single moving cursor is not enough: once the
    // overflow fallback jumps back to an earlier run, a forward-walking cursor
    // re-enters runs that already hold tenants with its counter reset to zero
    // and packs shops straight through each other.
    const buckets = STRIPS.map(() => []);
    const bucketUsed = STRIPS.map(() => 0);
    const EPS = 0.001;

    inline.forEach((tenant) => {
        // Preferred: the first run still inside its proportional budget.
        let idx = -1;
        for (let i = 0; i < STRIPS.length; i++) {
            if (bucketUsed[i] + tenant.width <= stripLen(STRIPS[i]) * fillRatio + EPS) { idx = i; break; }
        }
        // Fallback: any run with literal room left. Bin-packing always wastes
        // the tail of a run, so some tenants land here.
        if (idx < 0) {
            for (let i = 0; i < STRIPS.length; i++) {
                if (bucketUsed[i] + tenant.width <= stripLen(STRIPS[i]) + EPS) { idx = i; break; }
            }
        }
        if (idx < 0) return;   // genuinely no room left on this plan
        buckets[idx].push(tenant);
        bucketUsed[idx] += tenant.width;
    });

    buckets.forEach((bucket, i) => {
        if (!bucket.length) return;
        const s = STRIPS[i];
        const runLength = bucket.reduce((sum, t) => sum + t.width, 0);
        let cursor = s.from + Math.max(0, (stripLen(s) - runLength) / 2);

        bucket.forEach((tenant) => {
            const start = cursor;
            cursor += tenant.width;

            const zNear = s.cross;                       // corridor-facing edge
            const zFar = s.cross + s.dir * s.depth;      // back of house
            const geom = { x: start, z: Math.min(zNear, zFar), w: tenant.width, d: s.depth };
            const frontage = { x: start + tenant.width / 2, z: zNear };

            placed.push({
                tenant,
                geom,
                frontage,
                corridor: { axis: s.corridorAxis, pos: s.corridorPos },
                stripId: s.id,
                isAnchor: false,
            });
        });
    });

    return placed;
}

/** Nearest named court, used for the human-readable location line. */
function nearestZone(cx, cz) {
    let best = ZONES[0];
    let bestD = Infinity;
    ZONES.forEach((zone) => {
        const d = Math.hypot(zone.x - cx, zone.z - cz);
        if (d < bestD) { bestD = d; best = zone; }
    });
    return { zone: best, distance: bestD };
}

function compassOf(cx, cz) {
    const ns = cz < -20 ? 'north' : cz > 20 ? 'south' : '';
    const ew = cx < -20 ? 'west' : cx > 20 ? 'east' : '';
    if (ns && ew) return `${ns}-${ew}`;
    return ns || ew || 'central';
}

// ── lease + performance ─────────────────────────────────────────────────────

const LEASE_TYPES = ['Fixed rent', 'Turnover rent', 'Hybrid (base + turnover)'];

const LEASING_MANAGERS = [
    { name: 'Fatima Al Mansoori', email: 'f.almansoori@egoal-mall.ae', phone: '+971 2 555 0182' },
    { name: 'Rahul Menon', email: 'r.menon@egoal-mall.ae', phone: '+971 2 555 0147' },
    { name: 'Sara Haddad', email: 's.haddad@egoal-mall.ae', phone: '+971 2 555 0163' },
    { name: 'James Okoro', email: 'j.okoro@egoal-mall.ae', phone: '+971 2 555 0119' },
];

const CLAUSE_POOL = [
    'Tenant break option at month 36, 6 months’ written notice.',
    'Category exclusivity within the court for the lease term.',
    'Annual base rent escalation of 4% on each anniversary.',
    'Turnover rent at 8% of gross sales above the natural breakpoint.',
    'Landlord relocation right with 90 days’ notice and fit-out contribution.',
    'Minimum trading hours aligned to mall hours; no dark-shop dressing.',
    'Rent-free period of 60 days from handover for fit-out.',
    'Personal / corporate guarantee equal to 6 months’ rent.',
    'Service charge capped at 6% year-on-year increase.',
    'Co-tenancy clause tied to two of the four anchor tenants trading.',
];

const PROMO_POOL = [
    'Buy 2 get 1 free on selected lines',
    'Up to 50% off end-of-season',
    'Mall loyalty members earn 3x points',
    'Free gift with purchase above AED 500',
    'Weekday happy hour 3pm–6pm',
    'Student discount 15% with valid ID',
    'Eid collection now in store',
    'New season arrivals — first look for members',
];

const HOURS_STANDARD = {
    weekday: '10:00 – 22:00',
    weekend: '10:00 – 24:00',
    label: 'Sun–Wed 10:00–22:00 · Thu–Sat 10:00–24:00',
};
const HOURS_FNB = {
    weekday: '08:00 – 23:00',
    weekend: '08:00 – 01:00',
    label: 'Sun–Wed 08:00–23:00 · Thu–Sat 08:00–01:00',
};

function addMonths(date, months) {
    const d = new Date(date.getTime());
    d.setMonth(d.getMonth() + months);
    return d;
}

export function isoDate(d) {
    if (!d) return null;
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function formatDateLong(d) {
    if (!d) return '—';
    const date = d instanceof Date ? d : new Date(d);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Trailing 12 months ending with the month before AS_OF. */
function trailingMonths() {
    const out = [];
    for (let i = 12; i >= 1; i--) {
        const d = addMonths(AS_OF, -i);
        out.push({ date: d, month: d.getMonth(), year: d.getFullYear(), label: `${MONTH_LABELS[d.getMonth()]} ${String(d.getFullYear()).slice(2)}` });
    }
    return out;
}

const TRAILING = trailingMonths();

function buildLease(unit, rng) {
    const econ = CATEGORY_ECONOMICS[unit.category] || CATEGORY_ECONOMICS['Services'];

    // Term: anchors sign long, inline shops 3–7 years.
    const termYears = unit.isAnchor ? Math.round(between(rng, 10, 20)) : Math.round(between(rng, 3, 7));
    const monthsElapsed = Math.round(between(rng, 2, termYears * 12 - 2));
    const start = addMonths(AS_OF, -monthsElapsed);
    const end = addMonths(start, termYears * 12);
    const remainingMonths = Math.max(0, Math.round((end - AS_OF) / (1000 * 60 * 60 * 24 * 30.44)));

    const rentPerSqm = Math.round(
        econ.rent * (FLOOR_FACTOR[unit.floor] || 1) * between(rng, 0.86, 1.16) * (unit.isAnchor ? ANCHOR_RENT_DISCOUNT : 1)
    );
    const baseRentAnnual = Math.round(rentPerSqm * unit.gla);
    const camPerSqm = Math.round(between(rng, 240, 420) * (unit.isAnchor ? 0.8 : 1));
    const serviceChargeAnnual = Math.round(camPerSqm * unit.gla);
    const type = unit.isAnchor ? 'Hybrid (base + turnover)' : pick(rng, LEASE_TYPES);

    const clauses = [];
    const pool = [...CLAUSE_POOL];
    const clauseCount = 3 + Math.floor(rng() * 3);
    for (let i = 0; i < clauseCount && pool.length; i++) {
        clauses.push(pool.splice(Math.floor(rng() * pool.length), 1)[0]);
    }

    return {
        startDate: start,
        endDate: end,
        termYears,
        remainingMonths,
        expiringSoon: remainingMonths <= 6,
        type,
        turnoverPct: type === 'Fixed rent' ? null : Math.round(between(rng, 6, 12)),
        rentPerSqm,
        baseRentAnnual,
        baseRentMonthly: Math.round(baseRentAnnual / 12),
        camPerSqm,
        serviceChargeAnnual,
        serviceChargeMonthly: Math.round(serviceChargeAnnual / 12),
        deposit: Math.round((baseRentAnnual / 12) * (unit.isAnchor ? 6 : 3)),
        clauses,
        manager: LEASING_MANAGERS[hashString(unit.code) % LEASING_MANAGERS.length],
        documentRef: `LSE-${unit.floor}-${unit.code.split('-')[1]}-${start.getFullYear()}`,
    };
}

function buildPerformance(unit, rng) {
    const econ = CATEGORY_ECONOMICS[unit.category] || CATEGORY_ECONOMICS['Services'];
    const quality = between(rng, 0.62, 1.48); // this tenant's own trading strength
    const densityAnnual = econ.sales * (FLOOR_FACTOR[unit.floor] || 1) * quality;
    const baseMonthly = (densityAnnual * unit.gla) / 12;

    // Underlying growth trend, applied across the trailing window.
    const growth = between(rng, -0.06, 0.14);

    const monthly = TRAILING.map((m, i) => {
        const trend = 1 + (growth * (i / 11));
        const noise = between(rng, 0.93, 1.07);
        return {
            label: m.label,
            month: m.month,
            year: m.year,
            revenue: Math.round(baseMonthly * SEASONALITY[m.month] * trend * noise),
        };
    });

    const ytd = monthly.slice(-7).reduce((s, m) => s + m.revenue, 0);   // Jan–Jul 2026
    const qtd = monthly.slice(-3).reduce((s, m) => s + m.revenue, 0);
    const last12 = monthly.reduce((s, m) => s + m.revenue, 0);
    const lastMonth = monthly[monthly.length - 1].revenue;
    const priorMonth = monthly[monthly.length - 2].revenue;

    const atv = Math.round(econ.atv * between(rng, 0.85, 1.2));
    const transactions = Math.max(1, Math.round(lastMonth / atv));
    const conversionRate = +(econ.conv * between(rng, 0.8, 1.2)).toFixed(1);
    const footfall30d = Math.round(transactions / (conversionRate / 100));

    return {
        monthly,
        last12,
        ytd,
        qtd,
        lastMonth,
        momPct: +(((lastMonth - priorMonth) / priorMonth) * 100).toFixed(1),
        yoyPct: +(((lastMonth - monthly[0].revenue) / monthly[0].revenue) * 100).toFixed(1),
        salesPerSqm: Math.round(last12 / unit.gla),
        footfall30d,
        transactions,
        conversionRate,
        atv,
        quality,
    };
}

function buildOperations(unit, rng) {
    const isFnb = unit.category === 'F&B';
    const slug = unit.name.toLowerCase().replace(/[^a-z0-9]+/g, '');
    const promoCount = rng() < 0.55 ? 1 + Math.floor(rng() * 2) : 0;
    const promos = [];
    const pool = [...PROMO_POOL];
    for (let i = 0; i < promoCount && pool.length; i++) {
        promos.push(pool.splice(Math.floor(rng() * pool.length), 1)[0]);
    }

    const fitOut = addMonths(AS_OF, -Math.round(between(rng, 4, 96)));
    const condition = +between(rng, 2.8, 5).toFixed(1);

    return {
        hours: isFnb ? HOURS_FNB : HOURS_STANDARD,
        phone: `+971 2 ${String(400 + Math.floor(rng() * 599))} ${String(1000 + Math.floor(rng() * 8999))}`,
        email: `${slug}@egoal-mall.ae`,
        website: `www.${slug}.com`,
        instagram: `@${slug}`,
        promotions: promos,
        staffCount: Math.max(2, Math.round(unit.gla / between(rng, 22, 55))),
        lastFitOut: fitOut,
        conditionRating: condition,
        conditionLabel: condition >= 4.4 ? 'Excellent' : condition >= 3.6 ? 'Good' : condition >= 3 ? 'Fair' : 'Needs attention',
        utilities: {
            electricityKwh: Math.round(unit.gla * between(rng, 14, 34)),
            waterM3: Math.round(unit.gla * between(rng, 0.2, 1.1)),
            costMonthly: Math.round(unit.gla * between(rng, 26, 62)),
        },
    };
}

// ── assembly ────────────────────────────────────────────────────────────────

function buildUnits() {
    const units = [];

    FLOORS.forEach((floor) => {
        const floorRng = makeRng(hashString(`floor|${floor.id}`));

        // Anchors first — they're cast, not drawn — then fill the runs.
        const anchorPlan = ANCHOR_PLAN[floor.id] || {};
        const anchors = Object.keys(anchorPlan).map((slot) => {
            const [name, category, status] = anchorPlan[slot];
            return { name, category, status, anchor: slot, width: 0 };
        });

        const capacity = STRIPS.reduce((sum, st) => sum + (st.to - st.from), 0);
        const roster = [...anchors, ...buildFloorRoster(floor, capacity, floorRng, anchors.map((a) => a.name))];
        const placed = layoutFloor(floor.id, roster);

        placed.forEach((p, idx) => {
            const { tenant, geom, frontage, isAnchor, stripId, corridor } = p;
            const code = `${floor.id}-${String(idx + 1).padStart(3, '0')}`;

            const cx = geom.x + geom.w / 2;
            const cz = geom.z + geom.d / 2;
            const { zone } = nearestZone(cx, cz);
            const status = tenant.status || 'Occupied';
            const isVacant = status === 'Vacant';

            const unit = {
                id: `${floor.id}-${idx}`,
                code,
                name: isVacant ? `Vacant Unit ${code}` : tenant.name,
                brand: isVacant ? null : tenant.name,
                category: tenant.category,
                floor: floor.id,
                floorName: floor.name,
                floorY: floor.y,
                status,
                isAnchor,
                stripId,
                geom,
                center: { x: cx, z: cz },
                frontage,
                corridor,
                zoneId: zone.id,
                zoneName: zone.name,
                gla: Math.round(geom.w * geom.d),
                color: CATEGORY_COLORS[tenant.category] || '#64748B',
                locationDescription: `${floor.name}, ${zone.name} — ${compassOf(cx, cz)} boulevard`,
            };

            units.push(unit);
        });
    });

    return units;
}

export const UNITS = buildCommercials(buildUnits());

// ── commercial model ────────────────────────────────────────────────────────

/**
 * Attach lease, trading and operations to ONE unit. Split out of buildUnits so
 * an imported floor plan gets exactly the same commercial layer as the demo
 * estate — the two must be interchangeable everywhere downstream.
 */
export function attachUnitCommercials(unit) {
    const rng = makeRng(hashString(`${unit.code}|${unit.name}`));
    const isVacant = unit.status === 'Vacant';

    // Vacant + Coming Soon units carry no trading history.
    unit.lease = isVacant ? null : buildLease(unit, rng);
    unit.performance = (isVacant || unit.status === 'Coming Soon') ? null : buildPerformance(unit, rng);
    unit.operations = isVacant ? null : buildOperations(unit, rng);

    if (isVacant) {
        const econ = CATEGORY_ECONOMICS[unit.category] || CATEGORY_ECONOMICS['Services'];
        unit.askingRentPerSqm = Math.round(econ.rent * (FLOOR_FACTOR[unit.floor] || 1) * 1.05);
        unit.vacantSinceMonths = Math.round(between(rng, 1, 14));
    }
    return unit;
}

/**
 * Rankings, risk and neighbours — all of which need the whole population, so
 * they run once over the finished list rather than per unit.
 */
export function buildCommercials(list) {
    list.forEach(attachUnitCommercials);
    rankUnits(list);
    return list;
}

function rankUnits(UNITS) {
    const trading = UNITS.filter((u) => u.performance);
    const mallTotal = trading.reduce((s, u) => s + u.performance.last12, 0);
    const mallAvgDensity = trading.reduce((s, u) => s + u.performance.salesPerSqm, 0) / (trading.length || 1);

    const byMall = [...trading].sort((a, b) => b.performance.last12 - a.performance.last12);
    byMall.forEach((u, i) => { u.performance.mallRank = i + 1; });

    CATEGORIES.forEach((cat) => {
        const inCat = trading.filter((u) => u.category === cat);
        if (!inCat.length) return;
        const avgDensity = inCat.reduce((s, u) => s + u.performance.salesPerSqm, 0) / inCat.length;
        const sorted = [...inCat].sort((a, b) => b.performance.salesPerSqm - a.performance.salesPerSqm);
        sorted.forEach((u, i) => {
            u.performance.categoryRank = i + 1;
            u.performance.categoryCount = inCat.length;
            u.performance.categoryAvgDensity = Math.round(avgDensity);
            u.performance.vsCategoryPct = +(((u.performance.salesPerSqm - avgDensity) / avgDensity) * 100).toFixed(1);
        });
    });

    trading.forEach((u) => {
        u.performance.mallCount = trading.length;
        u.performance.mallAvgDensity = Math.round(mallAvgDensity);
        u.performance.vsMallPct = +(((u.performance.salesPerSqm - mallAvgDensity) / mallAvgDensity) * 100).toFixed(1);
        u.performance.pctOfMallSales = +((u.performance.last12 / mallTotal) * 100).toFixed(2);
        u.performance.occupancyCostRatio = u.lease
            ? +(((u.lease.baseRentAnnual + u.lease.serviceChargeAnnual) / u.performance.last12) * 100).toFixed(1)
            : null;
    });

    // Vacancy risk: weak trading + high occupancy cost + near expiry + tired fit-out.
    UNITS.forEach((u) => {
        if (u.status === 'Vacant') { u.vacancyRisk = 100; u.riskLabel = 'Vacant'; return; }
        if (!u.performance || !u.lease) { u.vacancyRisk = 25; u.riskLabel = 'Low'; return; }
        let risk = 0;
        if (u.performance.vsCategoryPct < -25) risk += 34;
        else if (u.performance.vsCategoryPct < -10) risk += 20;
        else if (u.performance.vsCategoryPct < 5) risk += 8;
        if (u.performance.occupancyCostRatio > 22) risk += 28;
        else if (u.performance.occupancyCostRatio > 17) risk += 15;
        if (u.lease.remainingMonths <= 6) risk += 22;
        else if (u.lease.remainingMonths <= 12) risk += 12;
        if (u.performance.momPct < -8) risk += 10;
        if (u.operations && u.operations.conditionRating < 3.4) risk += 8;
        u.vacancyRisk = Math.min(98, risk);
        u.riskLabel = risk >= 60 ? 'High' : risk >= 35 ? 'Watch' : 'Low';
    });

    // Nearby complementary stores — same floor, nearest four trading neighbours.
    UNITS.forEach((u) => {
        u.nearby = UNITS
            .filter((o) => o.floor === u.floor && o.id !== u.id && o.status === 'Occupied')
            .map((o) => ({ unit: o, d: Math.hypot(o.center.x - u.center.x, o.center.z - u.center.z) }))
            .sort((a, b) => a.d - b.d)
            .slice(0, 4)
            .map((n) => ({ id: n.unit.id, code: n.unit.code, name: n.unit.name, category: n.unit.category, metres: Math.round(n.d) }));
    });
}

// ── lookups + aggregates ────────────────────────────────────────────────────

const UNIT_BY_ID = new Map(UNITS.map((u) => [u.id, u]));
export const getUnit = (id) => UNIT_BY_ID.get(id) || null;
export const unitsOnFloor = (floorId) => UNITS.filter((u) => u.floor === floorId);

export function buildMallStats(units = UNITS) {
    const total = units.length;
    const occupied = units.filter((u) => u.status === 'Occupied').length;
    const vacant = units.filter((u) => u.status === 'Vacant').length;
    const trading = units.filter((u) => u.performance);
    const leased = units.filter((u) => u.lease);

    const totalGla = units.reduce((s, u) => s + u.gla, 0);
    const occupiedGla = units.filter((u) => u.status !== 'Vacant').reduce((s, u) => s + u.gla, 0);
    const totalSales = trading.reduce((s, u) => s + u.performance.last12, 0);
    const totalRent = leased.reduce((s, u) => s + u.lease.baseRentAnnual, 0);
    const totalFootfall = trading.reduce((s, u) => s + u.performance.footfall30d, 0);

    const expiring6 = leased.filter((u) => u.lease.remainingMonths <= 6);
    const expiring12 = leased.filter((u) => u.lease.remainingMonths <= 12);
    const atRisk = units.filter((u) => u.riskLabel === 'High');

    // Mall-level trailing 12 — sum each unit's monthly series positionally.
    const monthlyTotals = TRAILING.map((m, i) => ({
        label: m.label,
        revenue: trading.reduce((s, u) => s + (u.performance.monthly[i] ? u.performance.monthly[i].revenue : 0), 0),
    }));

    const byCategory = {};
    trading.forEach((u) => {
        if (!byCategory[u.category]) byCategory[u.category] = { category: u.category, sales: 0, gla: 0, units: 0, color: u.color };
        byCategory[u.category].sales += u.performance.last12;
        byCategory[u.category].gla += u.gla;
        byCategory[u.category].units += 1;
    });

    return {
        totalUnits: total,
        occupiedUnits: occupied,
        vacantUnits: vacant,
        comingSoon: units.filter((u) => u.status === 'Coming Soon').length,
        underRenovation: units.filter((u) => u.status === 'Under Renovation').length,
        occupancyRate: total ? +((occupiedGla / totalGla) * 100).toFixed(1) : 0,
        totalGla,
        occupiedGla,
        totalSales,
        totalRent,
        totalFootfall,
        avgRentPerSqm: leased.length ? Math.round(totalRent / leased.reduce((s, u) => s + u.gla, 0)) : 0,
        avgSalesPerSqm: trading.length ? Math.round(totalSales / trading.reduce((s, u) => s + u.gla, 0)) : 0,
        avgOccupancyCost: trading.length
            ? +(trading.reduce((s, u) => s + (u.performance.occupancyCostRatio || 0), 0) / trading.length).toFixed(1)
            : 0,
        monthlyTotals,
        topPerformers: [...trading].sort((a, b) => b.performance.last12 - a.performance.last12).slice(0, 10),
        weakPerformers: [...trading].sort((a, b) => a.performance.vsCategoryPct - b.performance.vsCategoryPct).slice(0, 8),
        expiring6: expiring6.sort((a, b) => a.lease.remainingMonths - b.lease.remainingMonths),
        expiring12,
        atRisk: atRisk.sort((a, b) => b.vacancyRisk - a.vacancyRisk),
        byCategory: Object.values(byCategory).sort((a, b) => b.sales - a.sales),
    };
}

export const MALL_STATS = buildMallStats();

/** Sales/footfall/risk normalised to 0..1 across the floor — drives the heatmaps. */
export function heatValue(unit, mode, floorUnits) {
    if (mode === 'none') return null;
    const vals = floorUnits
        .map((u) => rawHeat(u, mode))
        .filter((v) => v !== null && !isNaN(v));
    if (!vals.length) return null;
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const v = rawHeat(unit, mode);
    if (v === null || isNaN(v)) return null;
    return max === min ? 0.5 : (v - min) / (max - min);
}

function rawHeat(u, mode) {
    if (mode === 'sales') return u.performance ? u.performance.salesPerSqm : 0;
    if (mode === 'footfall') return u.performance ? u.performance.footfall30d / Math.max(1, u.gla) : 0;
    if (mode === 'risk') return u.vacancyRisk;
    return null;
}
